import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter 
} from '../../components/ui/dialog';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  CalendarDays,
  CheckCircle,
  XCircle,
  User,
  Calendar,
  ArrowRight,
} from 'lucide-react';

const statusColors = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
};

const ClassScheduleRequests = () => {
  const [searchParams] = useSearchParams();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('status') || 'pending');
  
  // Dialogs
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [approveDialog, setApproveDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Form data
  const [adminNotes, setAdminNotes] = useState('');
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchRequests = async () => {
    try {
      const response = await api.get('/class-schedule-requests');
      setRequests(response.data);
    } catch (error) {
      toast.error('Failed to fetch schedule requests');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const getFilteredRequests = () => {
    if (activeTab === 'all') return requests;
    return requests.filter(r => r.status === activeTab);
  };

  const handleApprove = async () => {
    if (!selectedRequest) return;
    
    setActionLoading(true);
    try {
      await api.put(`/class-schedule-requests/${selectedRequest.id}/approve`, {
        admin_notes: adminNotes
      });
      toast.success('Schedule change approved');
      setApproveDialog(false);
      setSelectedRequest(null);
      setAdminNotes('');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !rejectReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    
    setActionLoading(true);
    try {
      await api.put(`/class-schedule-requests/${selectedRequest.id}/reject`, {
        admin_notes: rejectReason
      });
      toast.success('Schedule change request rejected');
      setRejectDialog(false);
      setSelectedRequest(null);
      setRejectReason('');
      fetchRequests();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject request');
    } finally {
      setActionLoading(false);
    }
  };

  const openDetailsDialog = (request) => {
    setSelectedRequest(request);
    setDetailsDialog(true);
  };

  const openApproveDialog = (request) => {
    setSelectedRequest(request);
    setAdminNotes('');
    setApproveDialog(true);
  };

  const openRejectDialog = (request) => {
    setSelectedRequest(request);
    setRejectReason('');
    setRejectDialog(true);
  };

  const counts = {
    pending: requests.filter(r => r.status === 'pending').length,
    approved: requests.filter(r => r.status === 'approved').length,
    rejected: requests.filter(r => r.status === 'rejected').length,
    all: requests.length,
  };

  if (loading) {
    return (
      <DashboardLayout title="Class Schedule Requests">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Class Schedule Requests">
      <div className="space-y-6" data-testid="schedule-requests-page">
        <p className="text-slate-600">
          Parents request alternate class dates when they have scheduling conflicts.
        </p>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="pending">
              Pending ({counts.pending})
            </TabsTrigger>
            <TabsTrigger value="approved">
              Approved ({counts.approved})
            </TabsTrigger>
            <TabsTrigger value="rejected">
              Rejected ({counts.rejected})
            </TabsTrigger>
            <TabsTrigger value="all">
              All ({counts.all})
            </TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            {getFilteredRequests().length === 0 ? (
              <Card>
                <CardContent className="p-8 lg:p-12 text-center">
                  <CalendarDays className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No {activeTab} schedule requests</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {getFilteredRequests().map(request => (
                  <Card key={request.id} className="border border-slate-200">
                    <CardContent className="p-4 lg:p-6">
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-heading font-bold text-lg">{request.player_name}</h3>
                            <Badge className={statusColors[request.status]}>
                              {request.status}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center gap-2 text-sm text-slate-600 mb-3">
                            <User className="h-4 w-4" />
                            Parent: {request.parent_name}
                          </div>

                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg">
                            <div>
                              <p className="text-sm font-medium text-slate-500 mb-1">Original Schedule</p>
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-red-500" />
                                <span className="font-medium">{request.original_date}</span>
                              </div>
                              <p className="text-sm text-slate-600 mt-1">
                                {request.original_class_name} ({request.original_class_day} {request.original_class_time})
                              </p>
                            </div>
                            
                            <div>
                              <p className="text-sm font-medium text-slate-500 mb-1">Requested Alternate</p>
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-green-500" />
                                <span className="font-medium">{request.alternate_date}</span>
                              </div>
                              <p className="text-sm text-slate-600 mt-1">
                                {request.alternate_class_name} ({request.alternate_class_day} {request.alternate_class_time})
                              </p>
                            </div>
                          </div>

                          {request.reason && (
                            <p className="text-sm text-slate-600 mt-3">
                              <span className="font-medium">Reason:</span> {request.reason}
                            </p>
                          )}

                          <p className="text-xs text-slate-400 mt-2">
                            Requested: {new Date(request.created_at).toLocaleString()}
                          </p>
                        </div>

                        <div className="flex flex-col gap-2">
                          {request.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                className="bg-green-500 hover:bg-green-600"
                                onClick={() => openApproveDialog(request)}
                                data-testid={`approve-${request.id}`}
                              >
                                <CheckCircle className="h-4 w-4 mr-1" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-200 hover:bg-red-50"
                                onClick={() => openRejectDialog(request)}
                                data-testid={`reject-${request.id}`}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Reject
                              </Button>
                            </>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => openDetailsDialog(request)}
                          >
                            View Details
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Details Dialog */}
        <Dialog open={detailsDialog} onOpenChange={setDetailsDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CalendarDays className="h-5 w-5 text-blue-500" />
                Schedule Change Details
              </DialogTitle>
            </DialogHeader>
            
            {selectedRequest && (
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-500">Player</p>
                    <p className="font-medium">{selectedRequest.player_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Parent</p>
                    <p className="font-medium">{selectedRequest.parent_name}</p>
                  </div>
                </div>

                <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <p className="text-xs text-slate-500">FROM</p>
                      <p className="font-medium">{selectedRequest.original_date}</p>
                      <p className="text-sm text-slate-600">{selectedRequest.original_class_name}</p>
                      <p className="text-xs text-slate-400">{selectedRequest.original_class_day} {selectedRequest.original_class_time}</p>
                    </div>
                    <ArrowRight className="h-5 w-5 text-slate-400" />
                    <div className="flex-1">
                      <p className="text-xs text-slate-500">TO</p>
                      <p className="font-medium">{selectedRequest.alternate_date}</p>
                      <p className="text-sm text-slate-600">{selectedRequest.alternate_class_name}</p>
                      <p className="text-xs text-slate-400">{selectedRequest.alternate_class_day} {selectedRequest.alternate_class_time}</p>
                    </div>
                  </div>
                </div>

                {selectedRequest.reason && (
                  <div>
                    <p className="text-sm text-slate-500">Reason</p>
                    <p className="text-sm">{selectedRequest.reason}</p>
                  </div>
                )}

                {selectedRequest.admin_notes && (
                  <div>
                    <p className="text-sm text-slate-500">Admin Notes</p>
                    <p className="text-sm">{selectedRequest.admin_notes}</p>
                  </div>
                )}

                <div className="flex justify-between text-xs text-slate-400">
                  <span>Created: {new Date(selectedRequest.created_at).toLocaleString()}</span>
                  {selectedRequest.reviewed_at && (
                    <span>Reviewed: {new Date(selectedRequest.reviewed_at).toLocaleString()}</span>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Approve Dialog */}
        <Dialog open={approveDialog} onOpenChange={setApproveDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-600">
                <CheckCircle className="h-5 w-5" />
                Approve Schedule Change
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <p className="text-sm text-slate-600">
                Approving this request will allow {selectedRequest?.player_name} to attend the alternate class on {selectedRequest?.alternate_date}.
              </p>

              <div className="space-y-2">
                <label className="text-sm font-medium">Notes (Optional)</label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Any notes for the parent..."
                  rows={3}
                  data-testid="approve-notes"
                />
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setApproveDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  className="bg-green-500 hover:bg-green-600"
                  onClick={handleApprove}
                  disabled={actionLoading}
                  data-testid="confirm-approve"
                >
                  {actionLoading ? 'Approving...' : 'Approve'}
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>

        {/* Reject Dialog */}
        <Dialog open={rejectDialog} onOpenChange={setRejectDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-red-600">
                <XCircle className="h-5 w-5" />
                Reject Schedule Change
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <p className="text-sm text-slate-600">
                Please provide a reason for rejecting {selectedRequest?.player_name}'s schedule change request.
              </p>

              <div className="space-y-2">
                <label className="text-sm font-medium">Reason *</label>
                <Textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Reason for rejection..."
                  rows={3}
                  data-testid="reject-reason"
                />
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setRejectDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  variant="destructive"
                  onClick={handleReject}
                  disabled={actionLoading || !rejectReason.trim()}
                  data-testid="confirm-reject"
                >
                  {actionLoading ? 'Rejecting...' : 'Reject'}
                </Button>
              </DialogFooter>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ClassScheduleRequests;
